import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc, increment } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

export default function MyRaffles() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('created');
  const [createdRaffles, setCreatedRaffles] = useState([]);
  const [participatingRaffles, setParticipatingRaffles] = useState([]);
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewingComprovante, setViewingComprovante] = useState(null);

  const fetchData = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      // Try with orderBy first (needs composite index), fallback to simple query
      let created = [];
      try {
        const createdQuery = query(
          collection(db, 'raffles'),
          where('createdBy', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const createdSnapshot = await getDocs(createdQuery);
        created = createdSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (indexErr) {
        console.warn('Index not ready, using fallback query:', indexErr.message);
        const fallbackQuery = query(
          collection(db, 'raffles'),
          where('createdBy', '==', currentUser.uid)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        created = fallbackSnapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      }
      setCreatedRaffles(created);

      // Fetch purchases by uid AND by email (to catch guest purchases made before account creation)
      try {
        const allPurchases = new Map();

        // 1. Query by buyerId (logged-in purchases)
        try {
          const byUidQuery = query(
            collection(db, 'purchases'),
            where('buyerId', '==', currentUser.uid),
            orderBy('createdAt', 'desc')
          );
          const byUidSnap = await getDocs(byUidQuery);
          byUidSnap.docs.forEach(d => allPurchases.set(d.id, { id: d.id, ...d.data() }));
        } catch (e) {
          // Fallback without orderBy
          const fallback = query(collection(db, 'purchases'), where('buyerId', '==', currentUser.uid));
          const snap = await getDocs(fallback);
          snap.docs.forEach(d => allPurchases.set(d.id, { id: d.id, ...d.data() }));
        }

        // 2. Query by buyerEmail (guest purchases with same email)
        if (currentUser.email) {
          try {
            const byEmailQuery = query(
              collection(db, 'purchases'),
              where('buyerEmail', '==', currentUser.email),
              orderBy('createdAt', 'desc')
            );
            const byEmailSnap = await getDocs(byEmailQuery);
            byEmailSnap.docs.forEach(d => {
              if (!allPurchases.has(d.id)) {
                allPurchases.set(d.id, { id: d.id, ...d.data() });
              }
            });
          } catch (e) {
            const fallback = query(collection(db, 'purchases'), where('buyerEmail', '==', currentUser.email));
            const snap = await getDocs(fallback);
            snap.docs.forEach(d => {
              if (!allPurchases.has(d.id)) {
                allPurchases.set(d.id, { id: d.id, ...d.data() });
              }
            });
          }
        }

        // Merge, deduplicate, sort
        const merged = Array.from(allPurchases.values()).sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        setParticipatingRaffles(merged);
      } catch (err) {
        console.log('No purchases yet:', err.message);
        setParticipatingRaffles([]);
      }

      try {
        const pendingQuery = query(
          collection(db, 'purchases'),
          where('raffleCreatorId', '==', currentUser.uid),
          where('status', '==', 'pending')
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        setPendingPurchases(pendingSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.log('No pending purchases:', err.message);
        setPendingPurchases([]);
      }
    } catch (error) {
      console.error('Error fetching raffles:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfirmPurchase = async (purchase) => {
    setActionLoading(purchase.id);
    try {
      await updateDoc(doc(db, 'purchases', purchase.id), {
        status: 'confirmed',
        confirmedAt: new Date().toISOString(),
      });

      const ticketsRef = collection(db, 'raffles', purchase.raffleId, 'tickets');
      const ticketsSnap = await getDocs(query(ticketsRef, where('purchaseId', '==', purchase.id)));
      for (const ticketDoc of ticketsSnap.docs) {
        await updateDoc(ticketDoc.ref, { status: 'confirmed' });
      }

      await updateDoc(doc(db, 'raffles', purchase.raffleId), {
        numbersSold: increment(purchase.selectedNumbers.length),
        totalRevenue: increment(purchase.totalAmount),
      });

      await fetchData();
    } catch (error) {
      console.error('Error confirming purchase:', error);
      alert('Erro ao confirmar compra. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectPurchase = async (purchase) => {
    if (!window.confirm(t('myRaffles.confirmReject'))) return;

    setActionLoading(purchase.id);
    try {
      await updateDoc(doc(db, 'purchases', purchase.id), {
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
      });

      const ticketsRef = collection(db, 'raffles', purchase.raffleId, 'tickets');
      const ticketsSnap = await getDocs(query(ticketsRef, where('purchaseId', '==', purchase.id)));
      for (const ticketDoc of ticketsSnap.docs) {
        await deleteDoc(ticketDoc.ref);
      }

      await fetchData();
    } catch (error) {
      console.error('Error rejecting purchase:', error);
      alert('Erro ao rejeitar compra. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="my-raffles-page">
      <h1>{t('nav.myRaffles')}</h1>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'created' ? 'active' : ''}`} onClick={() => setActiveTab('created')}>
          {t('myRaffles.myRafflesTab')}
        </button>
        <button className={`tab ${activeTab === 'participating' ? 'active' : ''}`} onClick={() => setActiveTab('participating')}>
          {t('myRaffles.myTicketsTab')}
        </button>
        {pendingPurchases.length > 0 && (
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            style={{ position: 'relative' }}
          >
            {t('myRaffles.pendingPayments')}
            <span style={{
              position: 'absolute', top: '-4px', right: '-4px',
              background: 'var(--danger)', color: 'white', borderRadius: '50%',
              width: '20px', height: '20px', fontSize: '0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700
            }}>
              {pendingPurchases.length}
            </span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="empty-state">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>...</div>
          <h3>{t('common.loading')}</h3>
        </div>
      ) : activeTab === 'created' ? (
        createdRaffles.length > 0 ? (
          <div className="raffles-grid">
            {createdRaffles.map(raffle => {
              const maxNum = raffle.numberType === 'unlimited' ? 100 : raffle.maxNumbers;
              const progressPercent = ((raffle.numbersSold || 0) / maxNum) * 100;
              const badgeClass = raffle.status === 'active' ? 'badge-active'
                : raffle.status === 'completed' ? 'badge-completed' : 'badge-pending';

              return (
                <div key={raffle.id} className="raffle-card">
                  <div className="raffle-card-image">
                    {raffle.imageUrl ? (
                      <img src={raffle.imageUrl} alt={raffle.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      raffle.emoji || '🎰'
                    )}
                    <span className={`raffle-card-badge ${badgeClass}`}>
                      {t(`raffle.${raffle.status}`) || raffle.status}
                    </span>
                  </div>

                  <div className="raffle-card-body">
                    <h3>{raffle.title}</h3>
                    <p>{raffle.description}</p>

                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${Math.min(progressPercent, 100)}%` }}></div>
                    </div>

                    <div className="raffle-card-meta">
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>
                          {raffle.numbersSold || 0}/{maxNum} {t('raffle.sold')}
                        </div>
                        <div className="raffle-price">
                          {formatCurrency(raffle.ticketPrice || 0, raffle.currency || 'BRL')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Link
                          to={`/manage/${raffle.id}`}
                          className="btn btn-sm"
                          style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #e17055, #fdcb6e)', color: 'white', border: 'none', fontWeight: 600 }}
                        >
                          {t('myRaffles.manage')}
                        </Link>
                        <Link
                          to={`/rifa/${raffle.slug || raffle.id}`}
                          className="btn btn-primary btn-sm"
                          style={{ textDecoration: 'none' }}
                        >
                          {t('myRaffles.view')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>{t('myRaffles.noRafflesCreated')}</h3>
            <p style={{ marginBottom: '1.5rem' }}>{t('myRaffles.createFirst')}</p>
            <Link to="/create" className="btn btn-primary">{t('raffle.create')}</Link>
          </div>
        )
      ) : activeTab === 'participating' ? (
        participatingRaffles.length > 0 ? (
          <div className="raffles-grid">
            {participatingRaffles.map(purchase => {
              const statusColor = purchase.status === 'confirmed' ? 'var(--success)'
                : purchase.status === 'pending' ? 'var(--warning, #f39c12)' : 'var(--danger)';

              return (
                <div key={purchase.id} className="raffle-card">
                  <div className="raffle-card-image">
                    🎟️
                    <span className="raffle-card-badge" style={{ background: statusColor, color: 'white' }}>
                      {t(`raffle.${purchase.status}`) || purchase.status}
                    </span>
                  </div>

                  <div className="raffle-card-body">
                    <h3>{purchase.raffleTitle || 'Rifa'}</h3>

                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                      {(purchase.selectedNumbers || []).map(num => (
                        <span key={num} style={{
                          background: 'var(--primary)', color: 'white',
                          padding: '0.3rem 0.7rem', borderRadius: 'var(--radius-sm)',
                          fontWeight: 600, fontSize: '0.85rem'
                        }}>
                          {num}
                        </span>
                      ))}
                    </div>

                    <div className="raffle-card-meta">
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>
                          {(purchase.selectedNumbers || []).length} {t('myRaffles.numbers')}
                        </div>
                        <div className="raffle-price">
                          {formatCurrency(purchase.totalAmount || 0, purchase.currency || 'BRL')}
                        </div>
                      </div>
                      <Link
                        to={`/raffle/${purchase.raffleId}`}
                        className="btn btn-primary btn-sm"
                        style={{ textDecoration: 'none' }}
                      >
                        {t('myRaffles.viewRaffle')}
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🎲</div>
            <h3>{t('myRaffles.noTickets')}</h3>
            <p style={{ marginBottom: '1.5rem' }}>{t('myRaffles.browseRaffles')}</p>
            <Link to="/raffles" className="btn btn-primary">{t('myRaffles.browseAvailable')}</Link>
          </div>
        )
      ) : (
        pendingPurchases.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {pendingPurchases.map(purchase => {
              const isProcessing = actionLoading === purchase.id;

              return (
                <div key={purchase.id} style={{
                  background: 'var(--white)', borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem', boxShadow: 'var(--shadow-md)',
                  border: '2px solid var(--warning, #f39c12)', opacity: isProcessing ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 0.5rem' }}>{purchase.raffleTitle}</h3>
                      <p style={{ margin: '0 0 0.3rem', color: 'var(--dark-light)', fontSize: '0.9rem' }}>
                        {t('myRaffles.buyer')}: <strong>{purchase.buyerName || purchase.buyerEmail}</strong>
                      </p>
                      <p style={{ margin: '0 0 0.3rem', color: 'var(--dark-light)', fontSize: '0.9rem' }}>
                        📧 {purchase.buyerEmail || '-'}
                      </p>
                      <p style={{ margin: '0 0 0.5rem', color: 'var(--dark-light)', fontSize: '0.9rem' }}>
                        🎯 {t('myRaffles.numbers')}: {(purchase.selectedNumbers || []).join(', ')}
                      </p>
                      <p style={{ margin: '0 0 0.75rem', fontWeight: 700, fontSize: '1.1rem', color: 'var(--primary)' }}>
                        {formatCurrency(purchase.totalAmount || 0, purchase.currency || 'BRL')}
                      </p>

                      {/* Comprovante button */}
                      {purchase.comprovanteUrl && (
                        <button
                          onClick={() => setViewingComprovante(purchase.comprovanteUrl)}
                          style={{
                            background: 'linear-gradient(135deg, rgba(108,92,231,0.1), rgba(0,206,201,0.1))',
                            border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)',
                            padding: '0.4rem 0.8rem', cursor: 'pointer', fontWeight: 600,
                            color: 'var(--primary)', fontSize: '0.85rem', marginRight: '0.5rem',
                          }}
                        >
                          📎 Ver Comprovante
                        </button>
                      )}
                      <Link
                        to={`/manage/${purchase.raffleId}`}
                        style={{
                          display: 'inline-block', fontSize: '0.85rem', color: 'var(--primary)',
                          fontWeight: 600, textDecoration: 'none',
                        }}
                      >
                        ⚙️ Gerenciar Rifa
                      </Link>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'center' }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleConfirmPurchase(purchase)}
                        disabled={isProcessing}
                        style={{ background: 'var(--success)', color: 'white', border: 'none', padding: '0.5rem 1rem', fontWeight: 600 }}
                      >
                        {isProcessing ? '...' : t('myRaffles.confirm')}
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleRejectPurchase(purchase)}
                        disabled={isProcessing}
                        style={{ background: 'var(--danger)', color: 'white', border: 'none', padding: '0.5rem 1rem', fontWeight: 600 }}
                      >
                        {isProcessing ? '...' : t('myRaffles.reject')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">✅</div>
            <h3>{t('myRaffles.noPending')}</h3>
            <p>{t('myRaffles.allProcessed')}</p>
          </div>
        )
      )}

      {/* Comprovante Modal */}
      {viewingComprovante && (
        <div className="modal-overlay" onClick={() => setViewingComprovante(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', textAlign: 'center' }}>
            <h2>📎 Comprovante de Pagamento</h2>
            <img
              src={viewingComprovante}
              alt="Comprovante"
              style={{
                width: '100%', maxHeight: '70vh', objectFit: 'contain',
                borderRadius: 'var(--radius-md)', marginBottom: '1rem',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <a href={viewingComprovante} target="_blank" rel="noopener noreferrer"
                className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
                Abrir em Nova Aba
              </a>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewingComprovante(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
